import { FilterIcon, XIcon } from "lucide-react"
import type * as React from "react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerPicker } from "@/features/invoices/DocumentPickers"
import { useCustomer } from "@/features/customers/use-customer"
import type { useInvoiceFilters } from "@/features/invoices/use-invoice-list"
import { Badge } from "@/components/ui/badge"
import { DateField } from "@/components/DateField"
import { RupeeBound } from "@/components/RupeeBound"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DATE_PRESETS, activeDatePreset } from "@/lib/date-presets"
import { formatRupees } from "@/lib/money"

/*
 * THE 44px CONDITION WAS WITHDRAWN BY THE OWNER ON 2026-08-16, deliberately and
 * not by tidying — the comment that stood here asked not to be deleted silently,
 * so this is the record.
 *
 * These Selects replaced native `<select>`s, and the migration was granted on
 * condition the items stayed a real 44px touch target, because a phone's OS
 * wheel is big and reliably hittable and a cramped in-page listbox is not. That
 * reasoning was sound in the abstract and wrong in this app: every other menu
 * here — Quick Create in the sidebar — is a `DropdownMenu` at ~28px, which the
 * owner uses daily without complaint. Against it the 44px Select looked bulky,
 * which he said twice, comparing the two directly.
 *
 * So the rule is now CONSISTENCY, not a number: `SelectItem` matches
 * `DropdownMenuItem`, and the height lives in components/ui/select.tsx. If the
 * targets are ever raised, both are raised in the same change — one of them
 * alone is the bug.
 */

/*
 * AND THE TRIGGER MUST SAY THE OPTION'S WORDS, NOT ITS VALUE.
 *
 * `Select.Value` does NOT read the chosen item's text. It resolves a label
 * through the root's `items` map (`internals/resolveValueLabel.js`), and with no
 * `items` it falls back to `stringifyAsLabel(value)` — the raw string. A native
 * <select> showed the chosen <option>'s words for free; a `<SelectValue />`
 * left to itself renders "true" here, and "SERVICE" or "27" on the screens that
 * carry enum values. That is a word from the wire, on the owner's screen.
 *
 * So the labels live in one map, passed to the root AND rendered as the items,
 * which is also the only arrangement in which the two cannot drift.
 */

/**
 * The register's filters, behind one button (§7, §11.1).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EVERY FILTER HERE IS ONE THE API ACTUALLY SUPPORTS, and the list is short
 * because `InvoiceListQuerySchema` is `.strict()`: `status`, `customerId`,
 * `from`, `to`, `minTotalPaise`, `maxTotalPaise`, `issuedManually`. Anything
 * else is a 400, not an ignored parameter, so a control for it would be a dead
 * control.
 *
 * `issuedManually` IS THE ONE FILTER STILL UNIQUE TO THIS SHEET (§6.1). The
 * amount range is no longer — the quotation and challan registers carry it too
 * now — but the hand-written flag records that a TAX INVOICE was written in the
 * owner's book and its number back-filled into the gap-free series, and neither
 * of those documents has such a paper series. `issued_manually` exists only on
 * `model Invoice`, so a copy of this control onto either other sheet would be a
 * dead control on a `.strict()` schema.
 *
 * "SEARCH BY NAME" IS A CUSTOMER PICKER, NOT A TEXT BOX. The toolbar's `q` box
 * searches the bill's OWN numbers and cannot reach the buyer: the customer lives
 * in `customer_snapshot`, a text-backed JSON column the server will not query
 * inside (CLAUDE.md). A name box here could therefore only filter the fifty rows
 * already fetched — it would behave perfectly on a short register and silently
 * miss every bill from page two onward, which is exactly when someone needs it.
 * The picker searches customers server-side (name, phone or GSTIN) and filters
 * the register by the `customerId` it returns, which searches the WHOLE book.
 *
 * THE DATE PRESETS LIVE IN `lib/date-presets`, not here. The quotation register
 * offers the same buttons, and two copies of April–March arithmetic is two
 * chances for one register to answer a different question from the other while
 * both buttons read "This year".
 * ─────────────────────────────────────────────────────────────────────────────
 */

export function InvoiceFilterSheet({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useInvoiceFilters>["filters"]
  setFilters: ReturnType<typeof useInvoiceFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pickingCustomer, setPickingCustomer] = useState(false)

  /*
   * Memoised because the root writes `items` into its store on every render; a
   * fresh object each time would mark the store dirty and re-render the value
   * for nothing.
   */
  const manualLabels = useMemo<Record<string, React.ReactNode>>(
    () => ({
      "": t("list.anyStatus"),
      true: t("list.onlyByHand"),
      false: t("list.onlyInApp"),
    }),
    [t],
  )

  /*
   * The URL holds only the id, so the NAME has to be fetched to show it. Without
   * this the button would read "Customer #4", which is the database's way of
   * identifying Lupin, not the owner's.
   */
  const chosen = useCustomer(filters.customerId === undefined ? undefined : String(filters.customerId))

  const activePreset = activeDatePreset(filters.from, filters.to)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setOpen(true)
        }}
      >
        <FilterIcon />
        {/* The count is the whole point of collapsing them: a filtered register
          * that LOOKS unfiltered is how someone concludes their bills are gone. */}
        {activeCount === 0
          ? t("list.moreFilters")
          : t("list.moreFiltersCount", { n: activeCount })}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("list.moreFilters")}</DialogTitle>
            <DialogDescription>{t("invoice.listSubtitle")}</DialogDescription>
          </DialogHeader>

          {/* ── Customer ─────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("lbl.customer")}</Label>
            {filters.customerId === undefined ? (
              <Button
                type="button"
                variant="outline"
                className="justify-start"
                onClick={() => {
                  setPickingCustomer(true)
                }}
              >
                {t("list.filterByCustomer")}
              </Button>
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-md border p-2">
                <span className="min-w-0 truncate text-sm">
                  {chosen.data?.name ?? t("msg.loading")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFilters({ customerId: undefined })
                  }}
                >
                  <XIcon className="size-4" />
                  <span className="sr-only">{t("list.clearCustomerFilter")}</span>
                </Button>
              </div>
            )}
          </div>

          {/* ── Bill date ────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-2">
            <Label>{t("list.billDate")}</Label>

            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  type="button"
                  variant={
                    activePreset?.id === preset.id ||
                    (preset.id === "any" &&
                      filters.from === undefined &&
                      filters.to === undefined)
                      ? "default"
                      : "outline"
                  }
                  onClick={() => {
                    setFilters(preset.range())
                  }}
                >
                  {t(preset.labelKey)}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-from" className="text-muted-foreground text-xs">
                  {t("list.dateFrom")}
                </Label>
                <DateField
                  id="filter-from"
                  clearable
                  value={filters.from ?? ""}
                  placeholder={t("list.dateFrom")}
                  onChange={(value) => {
                    setFilters({ from: value === "" ? undefined : value })
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="filter-to" className="text-muted-foreground text-xs">
                  {t("list.dateTo")}
                </Label>
                <DateField
                  id="filter-to"
                  clearable
                  value={filters.to ?? ""}
                  placeholder={t("list.dateTo")}
                  onChange={(value) => {
                    setFilters({ to: value === "" ? undefined : value })
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Amount ───────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("list.amountRange")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <RupeeBound
                id="invoice-filter-min-amount"
                label={t("list.amountFrom")}
                paise={filters.minTotalPaise}
                onChange={(minTotalPaise) => {
                  setFilters({ minTotalPaise })
                }}
              />
              <RupeeBound
                id="invoice-filter-max-amount"
                label={t("list.amountTo")}
                paise={filters.maxTotalPaise}
                onChange={(maxTotalPaise) => {
                  setFilters({ maxTotalPaise })
                }}
              />
            </div>
          </div>

          {/* ── §6.1 — the bills he wrote by hand ────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="filter-manual">{t("invoice.writtenByHand")}</Label>
            {/*
              * THE VALUE IS NOT A STRING IN STATE — it is `boolean | undefined`,
              * stringified in both directions so ""/"true"/"false" can be three
              * options. The "" arm MUST keep producing `undefined`, not "": a
              * filter of `issuedManually=` is a 400 against `.strict()`, not an
              * ignored parameter.
              *
              * Base UI reads value="" as NO VALUE (SelectRoot: `hasSelectedValue
              * = value != null && serializedValue !== ''`), so with nothing
              * chosen the trigger renders the PLACEHOLDER rather than the
              * "Any" item's text. Passing the SAME key as both the placeholder
              * and the "" item's label is what makes the trigger read
              * identically either way — exactly what the native select showed.
              */}
            <Select
              items={manualLabels}
              value={filters.issuedManually === undefined ? "" : String(filters.issuedManually)}
              onValueChange={(value) => {
                // `onValueChange` is typed to allow null as well as "" — both
                // mean "no filter", and neither may reach the query string.
                const chosen = value == null ? "" : String(value)
                setFilters({ issuedManually: chosen === "" ? undefined : chosen === "true" })
              }}
            >
              {/* The id lives on the TRIGGER: that is the <button role="combobox">
                * the <Label htmlFor="filter-manual"> above must name, and it is
                * what `getByLabelText` and a screen reader both resolve to. */}
              <SelectTrigger id="filter-manual" className="w-full">
                <SelectValue placeholder={t("list.anyStatus")} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(manualLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={activeCount === 0}
              onClick={() => {
                clearFilters()
              }}
            >
              {t("list.clearFilters")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpen(false)
              }}
            >
              {t("btn.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(customer) => {
          setFilters({ customerId: customer.id })
        }}
      />
    </>
  )
}

/*
 * `RupeeBound` USED TO LIVE HERE, module-private, and now lives in
 * `components/RupeeBound`. It moved the day the quotation and challan registers
 * grew the same amount range: a second copy of a money input is a second opinion
 * on whether an empty box means "no filter" or zero, and on whether rupees
 * become paise by splitting the string or by multiplying a float. One copy, one
 * answer.
 */

/** The filters currently on, as removable chips under the toolbar. */
export function ActiveFilterChips({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useInvoiceFilters>["filters"]
  setFilters: ReturnType<typeof useInvoiceFilters>["setFilters"]
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const chosen = useCustomer(
    filters.customerId === undefined ? undefined : String(filters.customerId),
  )

  const chips: { id: string; label: string; clear: () => void }[] = []

  if (filters.customerId !== undefined) {
    chips.push({
      id: "customer",
      label: chosen.data?.name ?? t("msg.loading"),
      clear: () => {
        setFilters({ customerId: undefined })
      },
    })
  }
  if (filters.from !== undefined || filters.to !== undefined) {
    chips.push({
      id: "dates",
      label: `${filters.from ?? "…"} → ${filters.to ?? "…"}`,
      clear: () => {
        setFilters({ from: undefined, to: undefined })
      },
    })
  }
  if (filters.minTotalPaise !== undefined || filters.maxTotalPaise !== undefined) {
    chips.push({
      id: "amount",
      label: `${filters.minTotalPaise === undefined ? "…" : formatRupees(filters.minTotalPaise)} → ${
        filters.maxTotalPaise === undefined ? "…" : formatRupees(filters.maxTotalPaise)
      }`,
      clear: () => {
        setFilters({ minTotalPaise: undefined, maxTotalPaise: undefined })
      },
    })
  }
  if (filters.issuedManually !== undefined) {
    chips.push({
      id: "manual",
      label: filters.issuedManually ? t("list.onlyByHand") : t("list.onlyInApp"),
      clear: () => {
        setFilters({ issuedManually: undefined })
      },
    })
  }

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        /*
         * Each chip is REMOVABLE where it is shown. Sending someone back into
         * the dialog to undo one filter is the reason people give up and clear
         * everything, losing the three they wanted.
         */
        <Badge key={chip.id} variant="secondary" className="gap-1 py-1 pr-1 pl-2">
          <span className="max-w-48 truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.clear}
            aria-label={t("list.clearFilters")}
            className="hover:bg-background/60 rounded-sm p-0.5"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  )
}
